import { useCallback } from "react";

interface UseModalOutsideClickProps {
  onClose: () => void;
  onBackdropClick?: (e: React.MouseEvent) => void;
}

interface UseModalOutsideClickReturn {
  handleBackdropClick: (e: React.MouseEvent) => void;
  handleModalDialogClick: (e: React.MouseEvent) => void;
}

/**
 * Custom hook to handle modal outside click logic
 * Extracts the logic for closing modal when clicking outside of it
 * 
 * @param onClose - Function to call when modal should close
 * @param onBackdropClick - Optional custom handler for backdrop clicks
 * @returns Object containing handleBackdropClick and handleModalDialogClick handlers
 */
const useModalOutsideClick = ({
  onClose,
  onBackdropClick,
}: UseModalOutsideClickProps): UseModalOutsideClickReturn => {
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (onBackdropClick) {
        onBackdropClick(e);
      } else {
        onClose();
      }
    },
    [onClose, onBackdropClick],
  );

  const handleModalDialogClick = useCallback(
    (e: React.MouseEvent) => {
      // Only close if clicking directly on the dialog container, not on children
      const target = e.target as HTMLElement;
      const currentTarget = e.currentTarget as HTMLElement;
      
      // Check if click is on the element itself (currentTarget) or if it has modal__dialog class
      if (target === currentTarget || target.classList.contains("modal__dialog")) {
        handleBackdropClick(e);
      }
    },
    [handleBackdropClick],
  );

  return {
    handleBackdropClick,
    handleModalDialogClick,
  };
};

export default useModalOutsideClick;